import { Heading, SimpleGrid, Spinner, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiOutlineCpuChip } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { useAgentTypes } from "@/components/layout/AgentTypesContext";
import AgentCatalogCard from "@/pages/models/components/AgentCatalogCard";
import { AGENT_CATALOG, catalogEntryForType, tileForDuplicate } from "@/pages/models/agentCatalog";

const GRID_COLUMNS = { base: 1, md: 2, xl: 4 };

const Section = ({ title, children }) => (
    <VStack w="full" align="stretch" gap={4}>
        <Heading as="h2" fontSize="md" fontWeight={700} color="gray.900">{title}</Heading>
        <SimpleGrid columns={GRID_COLUMNS} gap={4} w="full">{children}</SimpleGrid>
    </VStack>
);

const ModelsPage = () => {
    const { t } = useTranslation("models");
    const navigate = useNavigate();
    useSetPageHeader(t("modelsPage.headerTitle"), t("modelsPage.headerSubtitle"), <HiOutlineCpuChip />);
    const { agents, isLoading } = useAgentTypes();

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="purple.500" size="lg" />
            </VStack>
        );
    }

    const defaultText = (key, field) => t(`agentsCatalog.agents.${key}.${field}`);
    const seenPerType = {};
    const ownedTypes = new Set(agents.map((agent) => agent.type ?? "Recruiter"));
    const purchasable = AGENT_CATALOG.filter((entry) => entry.type && !ownedTypes.has(entry.type));
    const comingSoon = AGENT_CATALOG.filter((entry) => !entry.type);

    return (
        <VStack w="full" py={6} align="stretch" gap={8}>
            {agents.length > 0 && (
                <Section title={t("agentsCatalog.sections.mine")}>
                    {agents.map((agent) => {
                        const entry = catalogEntryForType(agent.type);
                        const indexAmongSameType = seenPerType[entry.key] ?? 0;
                        seenPerType[entry.key] = indexAmongSameType + 1;
                        return (
                            <AgentCatalogCard
                                key={agent.id}
                                state="owned"
                                name={agent.name || defaultText(entry.key, "name")}
                                description={agent.description?.trim() || defaultText(entry.key, "description")}
                                image={entry.image}
                                tile={tileForDuplicate(entry, indexAmongSameType)}
                                onAction={() => navigate(`/models/${agent.id}`)}
                            />
                        );
                    })}
                </Section>
            )}

            {purchasable.length > 0 && (
                <Section title={t("agentsCatalog.sections.available")}>
                    {purchasable.map((entry) => (
                        <AgentCatalogCard
                            key={entry.key}
                            state="buy"
                            name={defaultText(entry.key, "name")}
                            description={defaultText(entry.key, "description")}
                            image={entry.image}
                            tile={entry.tile}
                            onAction={() => navigate(`/subscriptions?agent=${entry.type}`)}
                        />
                    ))}
                </Section>
            )}

            <Section title={t("agentsCatalog.sections.soon")}>
                {comingSoon.map((entry) => (
                    <AgentCatalogCard
                        key={entry.key}
                        state="soon"
                        name={defaultText(entry.key, "name")}
                        description={defaultText(entry.key, "description")}
                        image={entry.image}
                        tile={entry.tile}
                    />
                ))}
            </Section>
        </VStack>
    );
};

export default ModelsPage;
